import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchMemberCategory } from "../../api/memberApi";

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}


function SubTable({ title, headers, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              {headers.map((h) => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((cells, i) => (
              <TableRow key={i}>
                {cells.map((c, j) => <TableCell key={j}>{c || "—"}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function MemberCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItem(await fetchMemberCategory(categoryId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/member-categories`)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("memberCategory.detail.title")}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <DetailRow label={t("memberCategory.name")} value={item.memberCategoryName} />
        <DetailRow label={t("memberCategory.memberType")} value={item.memberTypeName} />
        <DetailRow label={t("memberCategory.description")} value={item.memberCategoryDescription} />
      </Paper>
      <SubTable
        title={t("memberCategory.classificationCriteriaValues")}
        headers={[t("memberCategory.classificationCriteriaName"), t("memberCategory.classificationCriteriaValueName")]}
        rows={(item.classificationCriteriaValues || []).map((v) => [v.classificationCriteria?.classificationCriteriaName, v.classificationCriteriaValueName])}
      />
    </Box>
  );
}
